// src/components/Questionnaires/HoursSpentPartF.js

import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Checkbox, Divider, Form, Input, message, Space, Spin, Typography} from 'antd';
import moment from 'moment';
import "./HoursSpentPartF.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const HoursSpentPartF = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalHours, setTotalHours] = useState(0);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/hours-spent-partF/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/hours-spent-partF/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                        calculateTotalHours(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Function to calculate total hours from the form values
    const calculateTotalHours = (values) => {
        let total = 0;

        // Iterate over all values to find hour-related fields
        Object.keys(values).forEach((key) => {
            const match = key.match(/How (many|much) hours.*\?/i);
            if (match && values[key]) {
                const hours = parseFloat(values[key]);
                if (!isNaN(hours)) {
                    total += hours;
                }
            }
        });

        setTotalHours(total);
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${server_url}/questionnaires/hours-spent-partF/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: values,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit Hours Spent Part F.');
            }

            const data = await response.json();
            message.success('Hours Spent Part F submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Hours Spent Part F:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Watch form changes to recalculate total hours
    const onValuesChange = (changedValues, allValues) => {
        calculateTotalHours(allValues);
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="learning-activities-container">
                <div className="part-f-container">
                    <h2 className="part-f-header">PART F</h2>
                    <h3 className="part-f-title">Number of Hours Spent by the Learner on the Following Activities:</h3>
                    <table className="part-f-table">
                        <thead>
                        <tr className="table-header">
                            <th>S. No.</th>
                            <th>Steps</th>
                            <th>Number of Hours Spent</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr className="table-section">
                            <td>1.</td>
                            <td>Group Project Work</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Research prompt/question/problem/challenge</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Guiding questions</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Stage 1 (Brainstorming and ideation)</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Stage 2 (Drafting, feedback, and revision)</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Stage 3 (Final submission)</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr className="table-total">
                            <td colSpan="2">Total Hours</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>

                        <tr className="table-section">
                            <td>2.</td>
                            <td>Problem-Based Inquiry (Individual Work)</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Project prompt/question/problem/challenge</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Hypothesis</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Guiding questions</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Evidence collection to support/negate hypothesis</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Analysis and synthesis</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Discussions</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Conclusion</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr className="table-total">
                            <td colSpan="2">Total Hours</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>

                        <tr className="table-section">
                            <td>3.</td>
                            <td>Classroom Interactions</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Classroom discussion</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Organized debate</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Simulation/roleplay</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Lab experiment</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Digital Learning</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        <tr className="table-total">
                            <td colSpan="2">Total Hours</td>
                            <td><input type="number" className="input-box"/></td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div className="skill-training-container">
                    <h2 className="skill-training-header">4. Learning outside the classroom (Skill training)</h2>
                    <table className="skill-training-table">
                        <thead>
                        <tr>
                            <th>&nbsp;</th>
                            <th>Skill</th>
                            <th>Number of Hours</th>
                            <th>Pursuing</th>
                            <th>Completed (✓)</th>
                        </tr>
                        </thead>
                        <tbody>
                        {["a", "b", "c", "d"].map((letter) => (
                            <tr key={letter}>
                                <td>{letter})</td>
                                <td><input type="text" className="input-box" placeholder="Enter skill here..."/></td>
                                <td><input type="number" className="input-box" min="0"/></td>
                                <td className="checkbox-center"><input type="checkbox"/></td>
                                <td className="checkbox-center"><input type="checkbox"/></td>
                            </tr>
                        ))}
                        </tbody>
                        <tfoot>
                        <tr>
                            <td colSpan="2">Total Hours</td>
                            <td><input type="number" className="input-box" min="0"/></td>
                            <td colSpan="2"></td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        onValuesChange={onValuesChange}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* Group Project Work *!/*/}
            {/*        <Title level={4}>Group Project Work</Title>*/}
            {/*        <Divider/>*/}

            {/*        /!* Research Prompt *!/*/}
            {/*        <Title level={5}>Research Prompt</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How many hours did you spend on identifying the research prompt/question/problem/challenge or planning the final output?"*/}
            {/*            name="How many hours did you spend on identifying the research prompt/question/problem/challenge or planning the final output?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What challenges did you face during this stage?"*/}
            {/*            name="What challenges did you face during this stage?"*/}
            {/*            rules={[{required: true, message: 'Please describe the challenges.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the challenges faced."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were there any resources you found particularly helpful in formulating the research prompt?"*/}
            {/*            name="Were there any resources you found particularly helpful in formulating the research prompt?"*/}
            {/*            rules={[{required: true, message: 'Please describe the helpful resources.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the resources that were helpful."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Guiding Questions *!/*/}
            {/*        <Title level={5}>Guiding Questions</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How much time did you allocate to creating guiding questions?"*/}
            {/*            name="How much time did you allocate to creating guiding questions?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did these questions help streamline your project work? How?"*/}
            {/*            name="Did these questions help streamline your project work? How?"*/}
            {/*            rules={[{required: true, message: 'Please describe how the questions helped.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="Describe how the questions helped streamline your project work."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Stage 1: Brainstorming and Ideation *!/*/}
            {/*        <Title level={4}>Stage 1: Brainstorming and Ideation</Title>*/}
            {/*        <Divider/>*/}

            {/*        <Form.Item*/}
            {/*            label="How many hours did you dedicate to brainstorming and ideating?"*/}
            {/*            name="How many hours did you dedicate to brainstorming and ideating?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What new ideas or approaches did you generate during this stage?"*/}
            {/*            name="What new ideas or approaches did you generate during this stage?"*/}
            {/*            rules={[{required: true, message: 'Please describe the new ideas or approaches.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the new ideas or approaches."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Stage 2: Drafting, Feedback, and Revision *!/*/}
            {/*        <Title level={4}>Stage 2: Drafting, Feedback, and Revision</Title>*/}
            {/*        <Divider/>*/}

            {/*        <Form.Item*/}
            {/*            label="How many hours did you spend on drafting and revising based on feedback?"*/}
            {/*            name="How many hours did you spend on drafting and revising based on feedback?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What feedback was the most impactful, and how did you incorporate it?"*/}
            {/*            name="What feedback was the most impactful, and how did you incorporate it?"*/}
            {/*            rules={[{*/}
            {/*                required: true,*/}
            {/*                message: 'Please describe the impactful feedback and its incorporation.'*/}
            {/*            }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the impactful feedback and how you incorporated it."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Stage 3: Final Submission *!/*/}
            {/*        <Title level={4}>Stage 3: Final Submission</Title>*/}
            {/*        <Divider/>*/}

            {/*        <Form.Item*/}
            {/*            label="How long did you work on preparing the final submission?"*/}
            {/*            name="How long did you work on preparing the final submission?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were there any final adjustments you made to ensure the quality of the output?"*/}
            {/*            name="Were there any final adjustments you made to ensure the quality of the output?"*/}
            {/*            rules={[{required: true, message: 'Please describe the final adjustments.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the final adjustments made."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Problem-Based Inquiry (Individual Work) *!/*/}
            {/*        <Title level={4}>Problem-Based Inquiry (Individual Work)</Title>*/}
            {/*        <Divider/>*/}

            {/*        /!* Research Prompt/Problem/Challenge *!/*/}
            {/*        <Title level={5}>Research Prompt/Problem/Challenge</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How much time did you spend on framing the research prompt or problem?"*/}
            {/*            name="How much time did you spend on framing the research prompt or problem?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What steps did you take to ensure the problem was clearly defined?"*/}
            {/*            name="What steps did you take to ensure the problem was clearly defined?"*/}
            {/*            rules={[{required: true, message: 'Please describe the steps taken.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the steps taken to define the problem."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Hypothesis Formulation *!/*/}
            {/*        <Title level={5}>Hypothesis Formulation</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How many hours did you dedicate to forming a hypothesis?"*/}
            {/*            name="How many hours did you dedicate to forming a hypothesis?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were there multiple hypotheses you considered before finalizing one?"*/}
            {/*            name="Were there multiple hypotheses you considered before finalizing one?"*/}
            {/*            rules={[{required: true, message: 'Please describe the hypotheses considered.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the hypotheses considered."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Guiding Questions *!/*/}
            {/*        <Title level={5}>Guiding Questions</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How much time was spent crafting guiding questions for your inquiry?"*/}
            {/*            name="How much time was spent crafting guiding questions for your inquiry?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How effective were these questions in helping you address the problem?"*/}
            {/*            name="How effective were these questions in helping you address the problem?"*/}
            {/*            rules={[{required: true, message: 'Please describe the effectiveness.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe how effective the questions were."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Evidence Collection *!/*/}
            {/*        <Title level={5}>Evidence Collection</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How many hours did you allocate to collecting evidence to support or negate your hypothesis?"*/}
            {/*            name="How many hours did you allocate to collecting evidence to support or negate your hypothesis?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What were the primary challenges you faced in evidence collection?"*/}
            {/*            name="What were the primary challenges you faced in evidence collection?"*/}
            {/*            rules={[{required: true, message: 'Please describe the challenges.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the challenges faced in evidence collection."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Analysis and Synthesis *!/*/}
            {/*        <Title level={5}>Analysis and Synthesis</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How much time was devoted to analyzing and synthesizing the collected data?"*/}
            {/*            name="How much time was devoted to analyzing and synthesizing the collected data?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the analysis lead to any unexpected findings?"*/}
            {/*            name="Did the analysis lead to any unexpected findings?"*/}
            {/*            rules={[{required: true, message: 'Please describe the findings.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe any unexpected findings."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Discussions and Conclusions *!/*/}
            {/*        <Title level={5}>Discussions and Conclusions</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How much time did you spend on discussions and formulating conclusions?"*/}
            {/*            name="How much time did you spend on discussions and formulating conclusions?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What was the most significant insight you gained from this process?"*/}
            {/*            name="What was the most significant insight you gained from this process?"*/}
            {/*            rules={[{required: true, message: 'Please describe the insight.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the most significant insight."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Classroom Interactions *!/*/}
            {/*        <Title level={4}>Classroom Interactions</Title>*/}
            {/*        <Divider/>*/}

            {/*        /!* Classroom Discussion *!/*/}
            {/*        <Title level={5}>Classroom Discussion</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How many hours did you spend participating in classroom discussions?"*/}
            {/*            name="How many hours did you spend participating in classroom discussions?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were there any moments where your perspective or knowledge changed due to the discussion?"*/}
            {/*            name="Were there any moments where your perspective or knowledge changed due to the discussion?"*/}
            {/*            rules={[{required: true, message: 'Please describe the moments.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the moments when your perspective changed."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Organized Debate *!/*/}
            {/*        <Title level={5}>Organized Debate</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How long did you prepare for and engage in debates?"*/}
            {/*            name="How long did you prepare for and engage in debates?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How did participating in debates enhance your understanding of the topic?"*/}
            {/*            name="How did participating in debates enhance your understanding of the topic?"*/}
            {/*            rules={[{required: true, message: 'Please describe the enhancement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe how debates enhanced your understanding."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Simulation/Role Play *!/*/}
            {/*        <Title level={5}>Simulation/Role Play</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How many hours were spent in simulation or role-play activities?"*/}
            {/*            name="How many hours were spent in simulation or role-play activities?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What key lessons or skills did you acquire during these activities?"*/}
            {/*            name="What key lessons or skills did you acquire during these activities?"*/}
            {/*            rules={[{required: true, message: 'Please describe the lessons or skills.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the lessons or skills acquired."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Lab Experiment *!/*/}
            {/*        <Title level={5}>Lab Experiment</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How much time was spent on conducting lab experiments?"*/}
            {/*            name="How much time was spent on conducting lab experiments?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were the results of the experiments consistent with your expectations?"*/}
            {/*            name="Were the results of the experiments consistent with your expectations?"*/}
            {/*            rules={[{required: true, message: 'Please describe the results.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the experiment results."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Digital Learning *!/*/}
            {/*        <Title level={5}>Digital Learning</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How many hours did you dedicate to digital learning sessions?"*/}
            {/*            name="How many hours did you dedicate to digital learning sessions?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0" placeholder="Enter hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Which tools or platforms were most effective for your learning experience?"*/}
            {/*            name="Which tools or platforms were most effective for your learning experience?"*/}
            {/*            rules={[{required: true, message: 'Please list the tools or platforms.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Zoom, Microsoft Teams, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Learning Outside the Classroom (Skill Training) *!/*/}
            {/*        <Title level={4}>Learning Outside the Classroom (Skill Training)</Title>*/}
            {/*        <Divider/>*/}

            {/*        /!* Skill *!/*/}
            {/*        <Title level={5}>Skill</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="What skills were you learning outside the classroom?"*/}
            {/*            name="What skills were you learning outside the classroom?"*/}
            {/*            rules={[{required: true, message: 'Please list the skills.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="List the skills you were learning."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How many hours were dedicated to developing each skill?"*/}
            {/*            name="How many hours were dedicated to developing each skill?"*/}
            {/*            rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter hours dedicated to each skill."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Pursuing/Completed *!/*/}
            {/*        <Title level={5}>Pursuing/Completed</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Which skills are you still pursuing, and which have you completed?"*/}
            {/*            name="Which skills are you still pursuing, and which have you completed?"*/}
            {/*            rules={[{required: true, message: 'Please describe the skills.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="Describe the skills you are pursuing and those you have completed."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How do you plan to use these skills in your academic or personal life?"*/}
            {/*            name="How do you plan to use these skills in your academic or personal life?"*/}
            {/*            rules={[{required: true, message: 'Please describe your plans.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe how you plan to use these skills."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Reflection *!/*/}
            {/*        <Title level={5}>Reflection</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How effective were the training sessions in helping you acquire the desired skill?"*/}
            {/*            name="How effective were the training sessions in helping you acquire the desired skill?"*/}
            {/*            rules={[{required: true, message: 'Please describe the effectiveness.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the effectiveness of the training sessions."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were there any additional resources you used to enhance your learning?"*/}
            {/*            name="Were there any additional resources you used to enhance your learning?"*/}
            {/*            rules={[{required: true, message: 'Please describe the additional resources.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe any additional resources used."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* General Reflection *!/*/}
            {/*        <Title level={4}>General Reflection</Title>*/}
            {/*        <Divider/>*/}

            {/*        <Form.Item*/}
            {/*            label="How do you feel about the overall distribution of time across these activities?"*/}
            {/*            name="How do you feel about the overall distribution of time across these activities?"*/}
            {/*            rules={[{required: true, message: 'Please describe your feelings.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe your feelings about time distribution."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What could you do differently in future projects to manage your time more effectively?"*/}
            {/*            name="What could you do differently in future projects to manage your time more effectively?"*/}
            {/*            rules={[{required: true, message: 'Please describe your strategies.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe strategies to manage time more effectively."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were there any activities that you feel could have been allocated more or less time?"*/}
            {/*            name="Were there any activities that you feel could have been allocated more or less time?"*/}
            {/*            rules={[{required: true, message: 'Please describe the activities.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="Describe activities that could have different time allocations."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Summary Table *!/*/}
            {/*        <Title level={4}>Summary Table</Title>*/}
            {/*        /!* Fixed Five Summary Entries *!/*/}
            {/*        <div>*/}
            {/*            {[1, 2, 3].map((num) => (*/}
            {/*                <div key={`summary_${num}`}*/}
            {/*                     style={{display: 'flex', justifyContent: 'space-between', marginBottom: '16px'}}>*/}
            {/*                    <Form.Item*/}
            {/*                        label={`Course ${num} Name`}*/}
            {/*                        name={`summary_course_name_${num}`}*/}
            {/*                        rules={[{required: true, message: 'Please enter the course name.'}]}*/}
            {/*                        style={{flex: 2, marginRight: '8px'}}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="Course Name"/>*/}
            {/*                    </Form.Item>*/}

            {/*                    <Form.Item*/}
            {/*                        label={`Course ${num} Number of Hours`}*/}
            {/*                        name={`summary_course_hours_${num}`}*/}
            {/*                        rules={[{required: true, message: 'Please enter the number of hours.'}]}*/}
            {/*                        style={{flex: 1, marginRight: '8px'}}*/}
            {/*                    >*/}
            {/*                        <Input type="number" min="0" placeholder="Number of Hours"/>*/}
            {/*                    </Form.Item>*/}
            {/*                </div>*/}
            {/*            ))}*/}
            {/*        </div>*/}

            {/*        <Form.Item*/}
            {/*            label="Total Hours:"*/}
            {/*            name="total_hours"*/}
            {/*            rules={[{required: true, message: 'Please enter the total number of hours.'}]}*/}
            {/*        >*/}
            {/*            <Input type="number" min="0"   value={totalHours}  placeholder="Total hours"/>*/}
            {/*        </Form.Item>*/}

            {/*          <Divider />*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}

            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={`${question}-${index}`} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default HoursSpentPartF;
